import React, { useState } from "react";
import {
  Card,
  Typography,
  Box,
  Grid,
  Button
} from "@mui/material";
import { BsXCircle, BsArrowRepeat, BsCopy, BsCheck2 } from "react-icons/bs";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { postRequest } from "../../libs/redux/apis/api";
import usericon from './user.png';
import './main.css';

export default function MoreInfo({
  css = {},
  icons = {},
  icon = 'thumbnail',
  skip = [],
  type,
  group = [],
  data = {},
  onClick,
  keys,
  onClose = () => { },
  isTimelapse = false
}) {
  const [copiedKey, setCopiedKey] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const handleCopy = (text, keyName) => {
    if (!text) return;
    window.navigator.clipboard.writeText(String(text))
      .then(() => {
        setCopiedKey(keyName);
        setTimeout(() => setCopiedKey(null), 2000);
      })
      .catch((err) => console.error("Failed to copy: ", err));
  };

  const authUser = useSelector((state) => state?.loginState?.auth);
  const canSyncVideo = Boolean(
    authUser?.is_superuser ||
    authUser?.main_role === "SuperAdmin" ||
    authUser?.permissions?.includes("camera.change_camera")
  );
  const isTimelapseCamera = Boolean(isTimelapse || data?.camera_type === "TIMELAPSE");
  const isLongVideoTicked = Boolean(
    data?.long_video === true ||
    data?.long_video === "Yes" ||
    data?.long_video === "true" ||
    data?.long_video === 1
  );

  const handleSyncLongVideo = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const camName = data?.camera_name || "this camera";
    const result = await Swal.fire({
      title: "Generate Long Video?",
      text: `This will compile all historical images for ${camName} from day 1 to today in the background.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Start Generation",
      confirmButtonColor: "#218b55",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setSyncing(true);
      try {
        const res = await postRequest(`timelapse/longvideo-sync/${data.id}/`, {});
        if (res?.is_success || res?.status === "success" || !res?.error) {
          Swal.fire({
            title: "Started!",
            text: res?.message || "Long video generation started in the background.",
            icon: "success",
            confirmButtonColor: "#218b55",
          });
        } else {
          Swal.fire({
            title: "Notice",
            text: res?.message || "Could not start long video generation.",
            icon: "warning",
            confirmButtonColor: "#218b55",
          });
        }
      } catch (err) {
        console.error("Sync long video error:", err);
        Swal.fire({
          title: "Error",
          text: "Could not connect to server.",
          icon: "error",
          confirmButtonColor: "#0c2a45",
        });
      } finally {
        setSyncing(false);
      }
    }
  };

  return (
    <Grid container>
      <Grid size={{ sm: 12, md: 12 }} sx={{ position: 'relative' }}>
        <BsXCircle
          style={{ position: 'absolute', right: 12, top: 12, cursor: 'pointer', zIndex: 10, fontSize: 18, color: '#94a3b8' }}
          onClick={() => onClose()}
        />
        <Card className="shadow rounded" sx={{ p: 2.5, mb: 1.5 }}>
          <Box display={'flex'} gap={2.5} flexWrap="wrap">
            <Box
              component="img"
              src={type === 'user' ? usericon : data[icon]}
              loading="lazy"
              alt=""
              sx={{
                width: 160,
                height: 115,
                objectFit: "cover",
                borderRadius: 2,
                bgcolor: "#0f172a"
              }}
            />
            <Box sx={{ flex: 1, minWidth: 260 }}>
              {group.filter(Boolean).map((key, i) => {
                const val = data[key];
                const isCopyable = Boolean(
                  icons[key] ||
                  ["access_location", "media_path", "sftp_path", "dir_path"].includes(key)
                );

                return (
                  <Box key={key} display={'flex'} alignItems="center" gap={1.5} sx={{ mb: 0.6, flexWrap: "wrap" }}>
                    <Typography fontSize={13} sx={{ minWidth: 120, textTransform: 'capitalize', color: '#64748b', fontWeight: 500 }}>
                      {key.replaceAll('_', ' ')}:
                    </Typography>
                    <Typography
                      color={i === 0 ? '#d99918' : 'inherit'}
                      fontWeight={600}
                      fontSize={i === 0 ? 14 : 12.5}
                      sx={{ wordBreak: "break-all", ...css[key] }}
                    >
                      {val || "—"}
                    </Typography>

                    {val && isCopyable && (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(val, key);
                        }}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          color: copiedKey === key ? "#16a34a" : "#2563eb",
                          px: 0.8,
                          py: 0.25,
                          borderRadius: 1.5,
                          bgcolor: copiedKey === key ? "rgba(22, 163, 74, 0.12)" : "rgba(37, 99, 235, 0.08)",
                          transition: "all 0.2s",
                          "&:hover": { bgcolor: "rgba(37, 99, 235, 0.18)" }
                        }}
                        title="Copy to clipboard"
                      >
                        {copiedKey === key ? <BsCheck2 size={13} /> : (icons[key] || <BsCopy size={12} />)}
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: copiedKey === key ? "#16a34a" : "inherit" }}>
                          {copiedKey === key ? "Copied!" : "Copy"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}

              {isTimelapseCamera && isLongVideoTicked && data?.id && canSyncVideo && (
                <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={syncing}
                    onClick={handleSyncLongVideo}
                    sx={{
                      background: "linear-gradient(135deg, #218b55 0%, #15693b 100%)",
                      color: "#fff",
                      textTransform: "none",
                      fontSize: "12px",
                      fontWeight: 600,
                      borderRadius: "20px",
                      py: 0.6,
                      px: 2,
                      boxShadow: "0 3px 10px rgba(33, 139, 85, 0.35)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      "&:hover": {
                        background: "linear-gradient(135deg, #1b7346 0%, #0f4f2c 100%)",
                        boxShadow: "0 4px 12px rgba(33, 139, 85, 0.45)",
                      }
                    }}
                  >
                    <BsArrowRepeat style={{ fontSize: 14 }} />
                    {syncing ? "Starting..." : "Sync / Generate Long Video"}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Card>

        <Card className="shadow rounded" sx={{ p: 2 }}>
          {Object.keys(data).map((key) => {
            if (group.includes(key)) return null;
            if (skip.includes(key)) return null;
            if (Array.isArray(data[key])) return null;
            if (typeof data[key] === 'object' && data[key] !== null) return null;

            const value = data[key];
            if (value === undefined || value === null || value === "") return null;

            // Check if value is an image URL or local image path
            const isImage = typeof value === "string" && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(value);
            const isPathOrUrl = typeof value === "string" && ["dir_path", "sftp_path", "media_path", "access_location", "stream_url", "rtsp_url", "hls_url", "ip_address", "url"].some(k => key.toLowerCase().includes(k));

            return (
              <Grid container spacing={1} sx={{ mb: 1.2, alignItems: "center" }} key={key}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography className="text" fontSize={13} sx={{ textTransform: 'capitalize', color: '#64748b' }}>
                    {key.replaceAll('_', ' ')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 9 }}>
                  {isImage ? (
                    <Box
                      component="img"
                      src={value}
                      alt={key}
                      loading="lazy"
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "4px",
                        objectFit: "cover",
                        border: "1px solid #ddd",
                      }}
                    />
                  ) : (
                    <Box display="inline-flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography className="text" fontWeight={600} fontSize={12} sx={{ wordBreak: "break-all" }}>
                        {String(value)}
                      </Typography>
                      {isPathOrUrl && (
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(value, key);
                          }}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            color: copiedKey === key ? "#16a34a" : "#64748b",
                            px: 0.6,
                            py: 0.2,
                            borderRadius: 1,
                            bgcolor: copiedKey === key ? "rgba(22, 163, 74, 0.1)" : "rgba(100, 116, 139, 0.08)",
                            transition: "all 0.2s",
                            "&:hover": { color: "#2563eb", bgcolor: "rgba(37, 99, 235, 0.12)" }
                          }}
                          title="Copy"
                        >
                          {copiedKey === key ? <BsCheck2 size={13} /> : <BsCopy size={12} />}
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: copiedKey === key ? "#16a34a" : "inherit" }}>
                            {copiedKey === key ? "Copied!" : "Copy"}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            );
          })}
        </Card>
      </Grid>
    </Grid>
  );
}
